import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import * as lawyersApi from '@/services/api/endpoints/lawyers'
import { LawyerDashboardStats, LawyerProfile, PublicLawyerProfile, UpdateLawyerProfilePayload } from '@/services/api/endpoints/lawyers'
import * as casesApi from '@/services/api/endpoints/cases'
import { BackendCase, CaseStats, CaseStatus, CaseType, CreateCasePayload, CreateTaskPayload, UpdateCasePayload, UpdateTaskPayload } from '@/services/api/endpoints/cases'
import * as appointmentsApi from '@/services/api/endpoints/appointments'
import { BackendAppointment, BookAppointmentPayload, BusySlot, UpdateAppointmentPayload, AppointmentStatus } from '@/services/api/endpoints/appointments'
import * as attorneyConnectionsApi from '@/services/api/endpoints/attorneyConnections'
import {
  ConnectionRequest,
  CreateConnectionRequestPayload,
  DeclineConnectionRequestPayload,
  RespondToConnectionRequestPayload,
  UpdateConsultationPayload,
  ReferralStatus,
} from '@/services/api/endpoints/attorneyConnections'
import { ApiError } from '@/services/api/types'

interface ImmigrationState {
  appointments: BackendAppointment[]
  appointmentsLoading: boolean
  busySlots: BusySlot[]
  busySlotsLoading: boolean
  caseDetail: BackendCase | null
  caseDetailLoading: boolean
  caseStats: CaseStats | null
  cases: BackendCase[]
  casesLoading: boolean
  connectionRequests: ConnectionRequest[]
  connectionRequestsLoading: boolean
  dashboardStats: LawyerDashboardStats | null
  dashboardStatsLoading: boolean
  error: string | null
  lawyerConnectionRequests: ConnectionRequest[]
  lawyerConnectionRequestsLoading: boolean
  profile: LawyerProfile | null
  profileLoading: boolean
  publicLawyerDetail: PublicLawyerProfile | null
  publicLawyerDetailLoading: boolean
  publicLawyers: PublicLawyerProfile[]
  publicLawyersLoading: boolean
  selectedAppointment: BackendAppointment | null
  selectedAppointmentLoading: boolean
  selectedConnectionRequest: ConnectionRequest | null
  selectedConnectionRequestLoading: boolean
}

const initialState: ImmigrationState = {
  appointments: [],
  appointmentsLoading: false,
  busySlots: [],
  busySlotsLoading: false,
  caseDetail: null,
  caseDetailLoading: false,
  caseStats: null,
  cases: [],
  casesLoading: false,
  connectionRequests: [],
  connectionRequestsLoading: false,
  dashboardStats: null,
  dashboardStatsLoading: false,
  error: null,
  lawyerConnectionRequests: [],
  lawyerConnectionRequestsLoading: false,
  profile: null,
  profileLoading: false,
  publicLawyerDetail: null,
  publicLawyerDetailLoading: false,
  publicLawyers: [],
  publicLawyersLoading: false,
  selectedAppointment: null,
  selectedAppointmentLoading: false,
  selectedConnectionRequest: null,
  selectedConnectionRequestLoading: false,
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const fetchLawyerProfile = createAsyncThunk('immigration/fetchLawyerProfile', async (_, { rejectWithValue }) => {
  try {
    return await lawyersApi.getLawyerProfile()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load lawyer profile'))
  }
})

export const updateLawyerProfileThunk = createAsyncThunk(
  'immigration/updateLawyerProfile',
  async (payload: UpdateLawyerProfilePayload, { rejectWithValue }) => {
    try {
      const profile = await lawyersApi.updateLawyerProfile(payload)
      toast.success('Profile updated')
      return profile
    } catch (error) {
      const message = errorMessage(error, 'Failed to update profile')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchLawyerDashboardStats = createAsyncThunk('immigration/fetchDashboardStats', async (_, { rejectWithValue }) => {
  try {
    return await lawyersApi.getLawyerDashboardStats()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load dashboard stats'))
  }
})

export const fetchCases = createAsyncThunk(
  'immigration/fetchCases',
  async (params: { limit?: number; page?: number; status?: CaseStatus; type?: CaseType } | undefined, { rejectWithValue }) => {
    try {
      const result = await casesApi.listCases(params)
      return result.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load cases'))
    }
  },
)

export const fetchCaseDetail = createAsyncThunk('immigration/fetchCaseDetail', async (id: string, { rejectWithValue }) => {
  try {
    return await casesApi.getCase(id)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load case'))
  }
})

export const fetchCaseStats = createAsyncThunk('immigration/fetchCaseStats', async (_, { rejectWithValue }) => {
  try {
    return await casesApi.getCaseStats()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load case stats'))
  }
})

export const createNewCase = createAsyncThunk(
  'immigration/createCase',
  async (payload: CreateCasePayload, { dispatch, rejectWithValue }) => {
    try {
      const created = await casesApi.createCase(payload)
      toast.success('Case created')
      dispatch(fetchCases(undefined))
      return created
    } catch (error) {
      const message = errorMessage(error, 'Failed to create case')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const updateCaseThunk = createAsyncThunk(
  'immigration/updateCase',
  async ({ id, payload }: { id: string; payload: UpdateCasePayload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await casesApi.updateCase(id, payload)
      toast.success('Case updated')
      dispatch(fetchCases(undefined))
      return updated
    } catch (error) {
      const message = errorMessage(error, 'Failed to update case')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const addCaseTask = createAsyncThunk(
  'immigration/addCaseTask',
  async ({ caseId, payload }: { caseId: string; payload: CreateTaskPayload }, { dispatch, rejectWithValue }) => {
    try {
      await casesApi.createCaseTask(caseId, payload)
      toast.success('Task added')
      return await dispatch(fetchCaseDetail(caseId)).unwrap()
    } catch (error) {
      const message = errorMessage(error, 'Failed to add task')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const updateCaseTaskThunk = createAsyncThunk(
  'immigration/updateCaseTask',
  async ({ caseId, taskId, payload }: { caseId: string; payload: UpdateTaskPayload; taskId: string }, { dispatch, rejectWithValue }) => {
    try {
      await casesApi.updateCaseTask(taskId, payload)
      return await dispatch(fetchCaseDetail(caseId)).unwrap()
    } catch (error) {
      const message = errorMessage(error, 'Failed to update task')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchAppointments = createAsyncThunk(
  'immigration/fetchAppointments',
  async (params: { limit?: number; page?: number; status?: AppointmentStatus } | undefined, { rejectWithValue }) => {
    try {
      const result = await appointmentsApi.listAppointments(params)
      return result.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load appointments'))
    }
  },
)

export const fetchAppointmentDetail = createAsyncThunk('immigration/fetchAppointmentDetail', async (id: string, { rejectWithValue }) => {
  try {
    return await appointmentsApi.getAppointment(id)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load appointment'))
  }
})

export const respondToAppointment = createAsyncThunk(
  'immigration/respondToAppointment',
  async ({ id, payload }: { id: string; payload: UpdateAppointmentPayload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await appointmentsApi.updateAppointment(id, payload)
      toast.success('Appointment updated')
      dispatch(fetchAppointments(undefined))
      return updated
    } catch (error) {
      const message = errorMessage(error, 'Failed to update appointment')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchLawyerBusySlots = createAsyncThunk('immigration/fetchBusySlots', async (lawyerProfileId: string, { rejectWithValue }) => {
  try {
    return await appointmentsApi.getLawyerBusySlots(lawyerProfileId)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load lawyer availability'))
  }
})

export const bookNewAppointment = createAsyncThunk(
  'immigration/bookAppointment',
  async (payload: BookAppointmentPayload, { rejectWithValue }) => {
    try {
      const appointment = await appointmentsApi.bookAppointment(payload)
      toast.success('Consultation booked')
      return appointment
    } catch (error) {
      const message = errorMessage(error, 'Failed to book consultation')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchPublicLawyers = createAsyncThunk(
  'immigration/fetchPublicLawyers',
  async (params: { focusArea?: string; limit?: number; page?: number } | undefined, { rejectWithValue }) => {
    try {
      const result = await lawyersApi.listPublicLawyers(params)
      return result.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load lawyers'))
    }
  },
)

export const fetchPublicLawyerDetail = createAsyncThunk('immigration/fetchPublicLawyerDetail', async (id: string, { rejectWithValue }) => {
  try {
    return await lawyersApi.getPublicLawyerProfile(id)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load lawyer profile'))
  }
})

// ─── Consultation requests (students and employers to attorneys) ─────────────

// The attorney's whole list at once (it is grouped into tabs on the page).
const LAWYER_LIST = { limit: 100 }

export const createConnectionRequestThunk = createAsyncThunk(
  'immigration/createConnectionRequest',
  async (payload: CreateConnectionRequestPayload, { rejectWithValue }) => {
    try {
      const request = await attorneyConnectionsApi.createConnectionRequest(payload)
      toast.success('Consultation request sent')
      return request
    } catch (error) {
      const message = errorMessage(error, 'Failed to send your consultation request')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchMyConnectionRequests = createAsyncThunk(
  'immigration/fetchMyConnectionRequests',
  async (params: { limit?: number; page?: number; status?: ReferralStatus } | undefined, { rejectWithValue }) => {
    try {
      const result = await attorneyConnectionsApi.listMyConnectionRequests(params)
      return result.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load consultation requests'))
    }
  },
)

export const fetchLawyerConnectionRequests = createAsyncThunk(
  'immigration/fetchLawyerConnectionRequests',
  async (params: { limit?: number; page?: number; status?: ReferralStatus } | undefined, { rejectWithValue }) => {
    try {
      const result = await attorneyConnectionsApi.listLawyerConnectionRequests({ ...LAWYER_LIST, ...params })
      return result.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load consultation requests'))
    }
  },
)

export const fetchConnectionRequestDetail = createAsyncThunk(
  'immigration/fetchConnectionRequestDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await attorneyConnectionsApi.getConnectionRequest(id)
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load the consultation request'))
    }
  },
)

export const acceptConnectionRequestThunk = createAsyncThunk(
  'immigration/acceptConnectionRequest',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const updated = await attorneyConnectionsApi.acceptConnectionRequest(id)
      toast.success('Request accepted. They now have your booking details.')
      dispatch(fetchLawyerConnectionRequests(undefined))
      return updated
    } catch (error) {
      const message = errorMessage(error, 'Failed to accept the request')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const declineConnectionRequestThunk = createAsyncThunk(
  'immigration/declineConnectionRequest',
  async ({ id, payload }: { id: string; payload: DeclineConnectionRequestPayload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await attorneyConnectionsApi.declineConnectionRequest(id, payload)
      toast.success('Request declined')
      dispatch(fetchLawyerConnectionRequests(undefined))
      return updated
    } catch (error) {
      const message = errorMessage(error, 'Failed to decline the request')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const respondToConnectionRequestThunk = createAsyncThunk(
  'immigration/respondToConnectionRequest',
  async ({ id, payload }: { id: string; payload: RespondToConnectionRequestPayload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await attorneyConnectionsApi.respondToConnectionRequest(id, payload)
      toast.success('Reply sent. You can carry on the conversation in Messages.')
      dispatch(fetchLawyerConnectionRequests(undefined))
      return updated
    } catch (error) {
      const message = errorMessage(error, 'Failed to send your reply')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const updateConsultationThunk = createAsyncThunk(
  'immigration/updateConsultation',
  async ({ id, payload }: { id: string; payload: UpdateConsultationPayload }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await attorneyConnectionsApi.updateConsultation(id, payload)
      toast.success({ CLOSED: 'Request closed', COMPLETED: 'Marked as completed', SCHEDULED: 'Marked as scheduled' }[payload.status])
      dispatch(fetchLawyerConnectionRequests(undefined))
      return updated
    } catch (error) {
      const message = errorMessage(error, 'Failed to update the consultation')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

const immigrationSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchLawyerProfile.pending, (state) => {
        state.profileLoading = true
      })
      .addCase(fetchLawyerProfile.fulfilled, (state, action) => {
        state.profile = action.payload
        state.profileLoading = false
      })
      .addCase(fetchLawyerProfile.rejected, (state, action) => {
        state.profileLoading = false
        state.error = action.payload as string
      })
      .addCase(updateLawyerProfileThunk.fulfilled, (state, action) => {
        state.profile = action.payload
      })
      .addCase(fetchLawyerDashboardStats.pending, (state) => {
        state.dashboardStatsLoading = true
      })
      .addCase(fetchLawyerDashboardStats.fulfilled, (state, action) => {
        state.dashboardStats = action.payload
        state.dashboardStatsLoading = false
      })
      .addCase(fetchLawyerDashboardStats.rejected, (state, action) => {
        state.dashboardStatsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchCases.pending, (state) => {
        state.casesLoading = true
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        state.cases = action.payload
        state.casesLoading = false
      })
      .addCase(fetchCases.rejected, (state, action) => {
        state.casesLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchCaseDetail.pending, (state) => {
        state.caseDetailLoading = true
      })
      .addCase(fetchCaseDetail.fulfilled, (state, action) => {
        state.caseDetail = action.payload
        state.caseDetailLoading = false
      })
      .addCase(fetchCaseDetail.rejected, (state, action) => {
        state.caseDetailLoading = false
        state.caseDetail = null
        toast.error(action.payload as string)
      })
      .addCase(fetchCaseStats.fulfilled, (state, action) => {
        state.caseStats = action.payload
      })
      .addCase(addCaseTask.fulfilled, (state, action) => {
        state.caseDetail = action.payload
      })
      .addCase(updateCaseTaskThunk.fulfilled, (state, action) => {
        state.caseDetail = action.payload
      })
      .addCase(fetchAppointments.pending, (state) => {
        state.appointmentsLoading = true
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.appointments = action.payload
        state.appointmentsLoading = false
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.appointmentsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchAppointmentDetail.pending, (state) => {
        state.selectedAppointmentLoading = true
      })
      .addCase(fetchAppointmentDetail.fulfilled, (state, action) => {
        state.selectedAppointment = action.payload
        state.selectedAppointmentLoading = false
      })
      .addCase(fetchAppointmentDetail.rejected, (state, action) => {
        state.selectedAppointmentLoading = false
        state.selectedAppointment = null
        toast.error(action.payload as string)
      })
      .addCase(respondToAppointment.fulfilled, (state, action) => {
        state.selectedAppointment = action.payload
      })
      .addCase(fetchLawyerBusySlots.pending, (state) => {
        state.busySlotsLoading = true
      })
      .addCase(fetchLawyerBusySlots.fulfilled, (state, action) => {
        state.busySlots = action.payload
        state.busySlotsLoading = false
      })
      .addCase(fetchLawyerBusySlots.rejected, (state, action) => {
        state.busySlotsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchPublicLawyers.pending, (state) => {
        state.publicLawyersLoading = true
      })
      .addCase(fetchPublicLawyers.fulfilled, (state, action) => {
        state.publicLawyers = action.payload
        state.publicLawyersLoading = false
      })
      .addCase(fetchPublicLawyers.rejected, (state, action) => {
        state.publicLawyersLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchPublicLawyerDetail.pending, (state) => {
        state.publicLawyerDetailLoading = true
      })
      .addCase(fetchPublicLawyerDetail.fulfilled, (state, action) => {
        state.publicLawyerDetail = action.payload
        state.publicLawyerDetailLoading = false
      })
      .addCase(fetchPublicLawyerDetail.rejected, (state, action) => {
        state.publicLawyerDetailLoading = false
        state.publicLawyerDetail = null
        toast.error(action.payload as string)
      })
      .addCase(fetchMyConnectionRequests.pending, (state) => {
        state.connectionRequestsLoading = true
      })
      .addCase(fetchMyConnectionRequests.fulfilled, (state, action) => {
        state.connectionRequests = action.payload
        state.connectionRequestsLoading = false
      })
      .addCase(fetchMyConnectionRequests.rejected, (state, action) => {
        state.connectionRequestsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchLawyerConnectionRequests.pending, (state) => {
        state.lawyerConnectionRequestsLoading = true
      })
      .addCase(fetchLawyerConnectionRequests.fulfilled, (state, action) => {
        state.lawyerConnectionRequests = action.payload
        state.lawyerConnectionRequestsLoading = false
      })
      .addCase(fetchLawyerConnectionRequests.rejected, (state, action) => {
        state.lawyerConnectionRequestsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchConnectionRequestDetail.pending, (state) => {
        state.selectedConnectionRequestLoading = true
      })
      .addCase(fetchConnectionRequestDetail.fulfilled, (state, action) => {
        state.selectedConnectionRequest = action.payload
        state.selectedConnectionRequestLoading = false
      })
      .addCase(fetchConnectionRequestDetail.rejected, (state, action) => {
        state.selectedConnectionRequestLoading = false
        state.selectedConnectionRequest = null
        toast.error(action.payload as string)
      })
      // the replies carry the request's own fields only: keep the people on it
      .addMatcher(
        (action) => [acceptConnectionRequestThunk.fulfilled.type, declineConnectionRequestThunk.fulfilled.type, updateConsultationThunk.fulfilled.type, respondToConnectionRequestThunk.fulfilled.type].includes(action.type),
        (state, action: { payload: ConnectionRequest; type: string }) => {
          const selected = state.selectedConnectionRequest
          if (selected && selected.id === action.payload.id) state.selectedConnectionRequest = { ...selected, ...action.payload, lawyer: selected.lawyer }
        },
      )
  },
  initialState,
  name: 'immigration',
  reducers: {
    clearCaseDetail: (state) => {
      state.caseDetail = null
    },
    clearSelectedAppointment: (state) => {
      state.selectedAppointment = null
    },
  },
})

export const { clearCaseDetail, clearSelectedAppointment } = immigrationSlice.actions

interface RootStateSlice {
  immigration: ImmigrationState
}

export const selectLawyerProfile = (state: RootStateSlice): LawyerProfile | null => state.immigration.profile
export const selectLawyerProfileLoading = (state: RootStateSlice): boolean => state.immigration.profileLoading
export const selectIsLawyerVerified = (state: RootStateSlice): boolean => state.immigration.profile?.verificationStatus === 'VERIFIED'
export const selectLawyerDashboardStats = (state: RootStateSlice): LawyerDashboardStats | null => state.immigration.dashboardStats
export const selectLawyerDashboardStatsLoading = (state: RootStateSlice): boolean => state.immigration.dashboardStatsLoading
export const selectCases = (state: RootStateSlice): BackendCase[] => state.immigration.cases
export const selectCasesLoading = (state: RootStateSlice): boolean => state.immigration.casesLoading
export const selectCaseDetail = (state: RootStateSlice): BackendCase | null => state.immigration.caseDetail
export const selectCaseDetailLoading = (state: RootStateSlice): boolean => state.immigration.caseDetailLoading
export const selectCaseStats = (state: RootStateSlice): CaseStats | null => state.immigration.caseStats
export const selectAppointments = (state: RootStateSlice): BackendAppointment[] => state.immigration.appointments
export const selectAppointmentsLoading = (state: RootStateSlice): boolean => state.immigration.appointmentsLoading
export const selectSelectedAppointment = (state: RootStateSlice): BackendAppointment | null => state.immigration.selectedAppointment
export const selectSelectedAppointmentLoading = (state: RootStateSlice): boolean => state.immigration.selectedAppointmentLoading
export const selectBusySlots = (state: RootStateSlice): BusySlot[] => state.immigration.busySlots
export const selectBusySlotsLoading = (state: RootStateSlice): boolean => state.immigration.busySlotsLoading
export const selectPublicLawyers = (state: RootStateSlice): PublicLawyerProfile[] => state.immigration.publicLawyers
export const selectPublicLawyersLoading = (state: RootStateSlice): boolean => state.immigration.publicLawyersLoading
export const selectPublicLawyerDetail = (state: RootStateSlice): PublicLawyerProfile | null => state.immigration.publicLawyerDetail
export const selectPublicLawyerDetailLoading = (state: RootStateSlice): boolean => state.immigration.publicLawyerDetailLoading
export const selectConnectionRequests = (state: RootStateSlice): ConnectionRequest[] => state.immigration.connectionRequests
export const selectConnectionRequestsLoading = (state: RootStateSlice): boolean => state.immigration.connectionRequestsLoading
export const selectLawyerConnectionRequests = (state: RootStateSlice): ConnectionRequest[] => state.immigration.lawyerConnectionRequests
export const selectLawyerConnectionRequestsLoading = (state: RootStateSlice): boolean => state.immigration.lawyerConnectionRequestsLoading
export const selectSelectedConnectionRequest = (state: RootStateSlice): ConnectionRequest | null => state.immigration.selectedConnectionRequest
export const selectSelectedConnectionRequestLoading = (state: RootStateSlice): boolean => state.immigration.selectedConnectionRequestLoading

export default immigrationSlice.reducer
